<template>
  <div class="video-list">
    <iframe
      id="video-iframe"
      width="100%"
      :height="iframeHeight"
      :src="`https://www.youtube.com/embed/F9F3xs_Nf3s?si=XnpUbZWdwUy6-7M0&amp;controls=0&enablejsapi=1&playsinline=1&origin=${origin}`"
      title="YouTube video player"
      frameborder="0"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
      referrerpolicy="strict-origin-when-cross-origin"
      allowfullscreen
    ></iframe>
    <div class="play-list">
      <div class="play-list-title">
        <img :src="require('@/assets/2025/images/07_listbox.png')" />
      </div>
      <div class="video-items-wrapper">
        <div v-for="video in videos" :key="video.id" class="video-item" :class="video.id % 3 === 0 ? 'break' : ''">
          <button @click.prevent.stop="handleVideoClick(video.id)">
            <img :src="video.thumbnail" :alt="video.description" class="video-thumbnail" />
          </button>
          <img :src="video.descriptionImage" class="video-thumbnail-desc" />
        </div>
      </div>
    </div>
    <LayerPopup :key="'videoListPopup'" ref="videoListPopup" class="video-popup" @mousedown.stop="handleMouseDown" @mouseup.stop="handleMouseUp" @touchstart.stop="handleTouchStart" @touchend.stop="handleTouchEnd" @on-close="handleOnCloseVideoPopup">
      <template v-if="currentVideoSrc">
        <button class="prev-btn" @click.prevent.stop="navigateVideo('prev')"></button>
        <div class="iframe-container" :class="{ 'fade-in': isFrameVisible, 'fade-out': !isFrameVisible }">
          <div class="iframe-content" :style="{ height: iframeListHeight + 'px' }">
            <iframe ref="videoListIframe" id="video-list-iframe" width="100%" :height="iframeListHeight" :src="currentVideoSrc" title="YouTube video player" frameborder="0" @load="handleIframeLoad" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; web-share" referrerpolicy="strict-origin-when-cross-origin"> </iframe>
            <!-- Transparent overlay to capture events while allowing YouTube controls to function -->
            <!-- <div id="video-overlay" class="iframe-overlay" :style="{ pointerEvents: overlayActive ? 'auto' : 'none' }" @mousedown.capture="handleMouseDown" @mouseup="handleMouseUp" @touchstart="handleTouchStart" @touchend="handleTouchEnd" @click="handleOverlayClick"></div> -->
          </div>
        </div>
        <button class="next-btn" @click.prevent.stop="navigateVideo('next')"></button>
      </template>
    </LayerPopup>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import LayerPopup from './LayerPopup.vue'

// Animation and timing constants
const FADE_TRANSITION_MS = 550
// const OVERLAY_TIMEOUT_MS = 300
const INITIAL_SIZE_CALC_DELAY_MS = 200
const FINAL_SIZE_CALC_DELAY_MS = 500
const YOUTUBE_API_INIT_DELAY_MS = 800
const QUICK_SIZE_CALC_DELAY_MS = 50
const QUICK_API_INIT_DELAY_MS = 100

// Window size variables
const windowWidth = ref(window.innerWidth)
const windowHeight = ref(window.innerHeight)
const iframeHeight = computed(() => (windowWidth.value < 640 ? '320' : '280'))
const iframeListHeight = ref('320')
const isFrameVisible = ref(true)
const overlayActive = ref(true) // Controls when overlay passes events to iframe
const origin = ref(window.location.origin)
// Component references and state
const videoListPopup = ref(null)
const currentVideoId = ref(null)
const videoListIframe = ref(null)
const currentVideoSrc = ref(null)
const youtubeApi = ref(null)

// Timer management
const clickTimer = ref(null)

const handleWindowResize = () => {
  windowWidth.value = window.innerWidth
  windowHeight.value = window.innerHeight

  // 리사이즈 시 현재 비디오 크기 업데이트
  if (currentVideoId.value) {
    const video = videos.value.find(v => v.id === currentVideoId.value)
    if (video) {
      updateVideoSize(video, false)
    }
  }
}

onMounted(() => {
  window.addEventListener('resize', handleWindowResize)
  setTimeout(() => initializeYoutubePlayer('video-iframe'), YOUTUBE_API_INIT_DELAY_MS)
})
onUnmounted(() => {
  window.removeEventListener('resize', handleWindowResize)
  if (clickTimer.value) {
    clearTimeout(clickTimer.value)
  }
})

const savedPosition = ref({
  x: 0,
  y: 0
})

const handleMouseDown = event => {
  savedPosition.value.x = event.x
  savedPosition.value.y = event.y
  overlayActive.value = true
}

const handleMouseUp = event => {
  if (Math.abs(event.x - savedPosition.value.x) > 50 && Math.abs(event.y - savedPosition.value.y) < 50) {
    if (event.x < savedPosition.value.x) {
      navigateVideo('next')
    } else {
      navigateVideo('prev')
    }
  }
  if (event.target.id === 'video-overlay') {
    toggleVideoPlayback()
  }
  savedPosition.value.x = 0
  savedPosition.value.y = 0
}

const toggleVideoPlayback = () => {
  if (youtubeApi.value) {
    const state = youtubeApi.value.getPlayerState()
    state === 1 ? youtubeApi.value.pauseVideo() : youtubeApi.value.playVideo()
  }
}

const handleTouchStart = event => {
  if (event.touches.length > 1) {
    return
  }
  savedPosition.value.x = event.touches[0].clientX
  savedPosition.value.y = event.touches[0].clientY
  overlayActive.value = true
}

const handleTouchEnd = event => {
  if (event.touches.length > 1) {
    return
  }
  const touchEndX = event.changedTouches[0].clientX
  const touchEndY = event.changedTouches[0].clientY

  // Detect horizontal swipe
  if (Math.abs(touchEndX - savedPosition.value.x) > 50 && Math.abs(touchEndY - savedPosition.value.y) < 50) {
    if (touchEndX < savedPosition.value.x) {
      navigateVideo('next')
    } else {
      navigateVideo('prev')
    }
  }

  // Toggle video playback if click was on overlay
  if (event.target.id === 'video-overlay') {
    toggleVideoPlayback()
  }

  savedPosition.value.x = 0
  savedPosition.value.y = 0
}

// // New function to handle overlay clicks
// const handleOverlayClick = () => {
//   // Temporarily disable the overlay to allow clicking YouTube controls
//   overlayActive.value = false

//   // Re-enable overlay after a short delay to allow the click to pass through
//   clearTimeout(clickTimer.value)
//   clickTimer.value = setTimeout(() => {
//     overlayActive.value = true
//   }, OVERLAY_TIMEOUT_MS)
// }

/**
 * Clean up resources and reset state when the video popup is closed
 */
const handleOnCloseVideoPopup = () => {
  // Clean up YouTube player
  try {
    const iframe = document.getElementById('video-list-iframe')
    // eslint-disable-next-line no-undef
    if (iframe?.contentWindow && window.YT?.get) {
      // eslint-disable-next-line no-undef
      const player = window.YT.get('video-list-iframe')
      player?.destroy?.()
    }
  } catch (e) {
    // Silently handle errors
  }

  // Reset state after component updates
  nextTick(() => {
    currentVideoSrc.value = null
    currentVideoId.value = null
    youtubeApi.value = null
    overlayActive.value = true

    // Clear any pending timers
    if (clickTimer.value) {
      clearTimeout(clickTimer.value)
      clickTimer.value = null
    }
  })
}

/**
 * Update the video size based on video orientation and window dimensions
 * @param {Object} video - The video object containing isVertical property
 * @param {boolean} shouldFade - Whether to apply fade transition effect
 */
const updateVideoSize = (video, shouldFade = false) => {
  if (!video) return

  // Apply fade-out effect if needed
  if (shouldFade) {
    isFrameVisible.value = false
  }

  // Get popup dimensions - use actual width if popup exists, otherwise estimate
  const popup = document.querySelector('.video-popup .layer-popup')
  const popupWidth = popup ? popup.offsetWidth : windowWidth.value * 0.95

  // Get content dimensions for more accurate measurement
  const iframeContent = popup ? popup.querySelector('.iframe-content') : null
  const contentWidth = iframeContent ? iframeContent.offsetWidth : popupWidth

  // Use the larger value for calculations
  const calculationWidth = Math.max(popupWidth, contentWidth)

  // Calculate height based on video orientation
  let frameHeight
  if (video.isVertical) {
    // Vertical video aspect ratio (396:703)
    frameHeight = Math.round((calculationWidth * 703) / 396)
  } else {
    // Horizontal video aspect ratio (16:9)
    frameHeight = Math.round((calculationWidth * 9) / 16)
  }

  // Apply height constraints
  const maxHeight = windowHeight.value * 0.85 // Maximum 85% of viewport height
  const minHeight = calculationWidth * 0.5 // Minimum half of width
  iframeListHeight.value = Math.min(Math.max(frameHeight, minHeight), maxHeight)

  // Handle fade-in transition
  if (shouldFade) {
    setTimeout(() => {
      isFrameVisible.value = true
    }, FADE_TRANSITION_MS)
  }
}

/**
 * Navigate to previous or next video
 * @param {string} direction - Either 'prev' or 'next'
 */
const navigateVideo = direction => {
  if (!currentVideoId.value) return

  // Find index of current video
  const currentIndex = videos.value.findIndex(v => v.id === currentVideoId.value)
  if (currentIndex === -1) return

  // Get current video for orientation comparison
  const currentVideo = videos.value[currentIndex]

  // Calculate next index with wraparound
  let nextIndex
  if (direction === 'next') {
    nextIndex = currentIndex === videos.value.length - 1 ? 0 : currentIndex + 1
  } else {
    // prev
    nextIndex = currentIndex === 0 ? videos.value.length - 1 : currentIndex - 1
  }

  const nextVideo = videos.value[nextIndex]

  // Apply fade effect if orientation changes (vertical/horizontal)
  const orientationChanged = currentVideo.isVertical !== nextVideo.isVertical

  // Play the new video with transition if orientation changed
  handleVideoClick(nextVideo.id, orientationChanged)
}

/**
 * Handle video selection and initialize playback
 * @param {number} videoId - The ID of the video to display
 * @param {boolean} useTransition - Whether to apply fade transition between videos
 */
const handleVideoClick = (videoId, useTransition = false) => {
  const video = videos.value.find(v => v.id === videoId)
  if (!video) return

  // Store current video ID and reset overlay state
  currentVideoId.value = videoId
  overlayActive.value = true

  // Clear any pending timers
  if (clickTimer.value) {
    clearTimeout(clickTimer.value)
  }

  // Update video size with fade effect if requested
  updateVideoSize(video, useTransition)

  // Build YouTube video URL with parameters
  const youtubeParams = 'autoplay=1&control=0&loop=1&enablejsapi=1&playsinline=1' + `&origin=${origin.value}`

  // Set video source with delay for transition if needed
  if (useTransition) {
    // Delay source change until fade-out animation is in progress
    setTimeout(() => {
      currentVideoSrc.value = `${video.videoSrc}&${youtubeParams}`
    }, Math.floor(FADE_TRANSITION_MS * 0.6)) // ~60% through the fade transition
  } else {
    // Set source immediately without transition
    currentVideoSrc.value = `${video.videoSrc}&${youtubeParams}`
  }

  // Open popup and initialize YouTube API
  nextTick(() => {
    if (!videoListPopup.value.visible) {
      videoListPopup.value.open()

      // Calculate size after popup is opened
      setTimeout(() => updateVideoSize(video, false), INITIAL_SIZE_CALC_DELAY_MS)

      // Final size calculation after animation completes
      setTimeout(() => updateVideoSize(video, false), FINAL_SIZE_CALC_DELAY_MS)

      // Initialize YouTube API after animation completes
      setTimeout(() => initializeYoutubePlayer('video-list-iframe'), YOUTUBE_API_INIT_DELAY_MS)
    } else {
      // Quick updates for already open popup
      setTimeout(() => updateVideoSize(video, false), QUICK_SIZE_CALC_DELAY_MS)
      setTimeout(() => initializeYoutubePlayer('video-list-iframe'), QUICK_API_INIT_DELAY_MS)
    }
  })
}

/**
 * Initialize the YouTube player API reference
 * @param {string} iframeId - The ID of the iframe containing the YouTube player
 */
const initializeYoutubePlayer = iframeId => {
  try {
    const iframe = document.getElementById(iframeId)
    // eslint-disable-next-line no-undef
    if (iframe?.contentWindow && window.YT?.get) {
      // eslint-disable-next-line no-undef
      const player = window.YT.get(iframeId)
      youtubeApi.value = player
    }
  } catch (e) {
    // Silently handle errors - avoid console errors
  }
}

/**
 * Handle when the iframe content is loaded
 */
const handleIframeLoad = () => {
  // Show iframe if it's currently hidden during transition
  if (!isFrameVisible.value) {
    isFrameVisible.value = true
  }
}

const videos = ref([
  {
    id: 1,
    description: 'EP.1 좋아하는 친구들과 함께',
    videoSrc: 'https://www.youtube.com/embed/gQPKnxQriDs?si=XyTDzE2swM8hSIsH&playlist=gQPKnxQriDs',
    thumbnail: require('@/assets/2025/images/07_img01.png'),
    descriptionImage: require('@/assets/2025/images/07_txt01.png'),
    isVertical: false
  },
  {
    id: 2,
    description: 'EP.2 혼자 집에서',
    videoSrc: 'https://www.youtube.com/embed/Wc93xdT1Jkg?si=waAuLeBsDLhU6wGL&playlist=Wc93xdT1Jkg',
    thumbnail: require('@/assets/2025/images/07_img02.png'),
    descriptionImage: require('@/assets/2025/images/07_txt02.png'),
    isVertical: false
  },
  {
    id: 3,
    description: 'EP.3 밖에서 여유롭게',
    videoSrc: 'https://www.youtube.com/embed/KqI9BqOEQf8?si=VfMyCV5a33EUFuYD&playlist=KqI9BqOEQf8',
    thumbnail: require('@/assets/2025/images/07_img03.png'),
    descriptionImage: require('@/assets/2025/images/07_txt03.png'),
    isVertical: false
  },
  {
    id: 4,
    description: 'EP.4 맑은 공기 마시며',
    videoSrc: 'https://www.youtube.com/embed/NfuDHSuzYAs?si=aj11UNaIEqYC8vIe&playlist=NfuDHSuzYAs',
    thumbnail: require('@/assets/2025/images/07_img04.png'),
    descriptionImage: require('@/assets/2025/images/07_txt04.png'),
    isVertical: false
  },
  {
    id: 5,
    description: 'EP.5 안 힘들 땐',
    videoSrc: 'https://www.youtube.com/embed/IKtInjaZjzA?si=BtjibTGzS2R09UK5&playlist=IKtInjaZjzA',
    thumbnail: require('@/assets/2025/images/07_img05.png'),
    descriptionImage: require('@/assets/2025/images/07_txt05.png'),
    isVertical: false
  },
  {
    id: 6,
    description: 'EP.6 언제 어디서나',
    videoSrc: 'https://www.youtube.com/embed/lr2EnfLsT5o?si=UL9e3r2NdCh6zSs8&playlist=lr2EnfLsT5o',
    thumbnail: require('@/assets/2025/images/07_img06.png'),
    descriptionImage: require('@/assets/2025/images/07_txt06.png'),
    isVertical: false
  },
  {
    id: 7,
    description: '15s 그거 잘 됐다',
    videoSrc: 'https://www.youtube.com/embed/4bKtm3HS4zM?si=0cRLGChzonKN7PKI&playlist=4bKtm3HS4zM',
    thumbnail: require('@/assets/2025/images/07_img07.png'),
    descriptionImage: require('@/assets/2025/images/07_txt07.png'),
    isVertical: false
  },
  {
    id: 8,
    description: '15s 청춘은 바로 이 순간',
    videoSrc: 'https://www.youtube.com/embed/4qJHaC8s0ZE?si=0MMKMVhfk4MaSKbS&playlist=4qJHaC8s0ZE',
    thumbnail: require('@/assets/2025/images/07_img08.png'),
    descriptionImage: require('@/assets/2025/images/07_txt08.png'),
    isVertical: false
  },
  {
    id: 9,
    description: 'shots 오히려 좋아',
    videoSrc: 'https://www.youtube.com/embed/AevH2MpJ0S0?si=kbiEzWvqSsmiKRJg&playlist=AevH2MpJ0S0',
    thumbnail: require('@/assets/2025/images/07_img09.png'),
    descriptionImage: require('@/assets/2025/images/07_txt09.png'),
    isVertical: true
  },
  {
    id: 10,
    description: 'shots 나만 몰랐던 이야기',
    videoSrc: 'https://www.youtube.com/embed/M9pQBs3Ja-k?si=ds7Eu2dfeh-yUhew&playlist=M9pQBs3Ja-k',
    thumbnail: require('@/assets/2025/images/07_img10.png'),
    descriptionImage: require('@/assets/2025/images/07_txt10.png'),
    isVertical: true
  },
  {
    id: 11,
    description: 'shots 있었는데요 없었습니다',
    videoSrc: 'https://www.youtube.com/embed/Qj7EJDOUfts?si=TOfU8ZQSk_m3X-L5&playlist=Qj7EJDOUfts',
    thumbnail: require('@/assets/2025/images/07_img11.png'),
    descriptionImage: require('@/assets/2025/images/07_txt11.png'),
    isVertical: true
  },
  {
    id: 12,
    description: 'shots 아유... 하기싫어...',
    videoSrc: 'https://www.youtube.com/embed/dTWVqMR9dfs?si=v6ITV7Nwn3FC17GN&playlist=dTWVqMR9dfs',
    thumbnail: require('@/assets/2025/images/07_img12.png'),
    descriptionImage: require('@/assets/2025/images/07_txt12.png'),
    isVertical: true
  },
  {
    id: 13,
    description: 'shots 사랑하는 엄마와 함께 짐빔 하이볼',
    videoSrc: 'https://www.youtube.com/embed/pJwiULGDJoE?si=mnzACg3cjWavH07W&playlist=pJwiULGDJoE',
    thumbnail: require('@/assets/2025/images/07_img13.png'),
    descriptionImage: require('@/assets/2025/images/07_txt13.png'),
    isVertical: true
  },
  {
    id: 14,
    description: 'shots Q. 지금 지쳤나요?',
    videoSrc: 'https://www.youtube.com/embed/-sko4SZOVlo?si=ETXDfMVcNJ14iO6R&playlist=-sko4SZOVlo',
    thumbnail: require('@/assets/2025/images/07_img14.png'),
    descriptionImage: require('@/assets/2025/images/07_txt14.png'),
    isVertical: true
  },
  {
    id: 15,
    description: 'shots 면접관 장원영 면접자 박정민',
    videoSrc: 'https://www.youtube.com/embed/o1oK2MqyGSU?si=bdn83BZCHOXW_HEL&playlist=o1oK2MqyGSU',
    thumbnail: require('@/assets/2025/images/07_img15.png'),
    descriptionImage: require('@/assets/2025/images/07_txt15.png'),
    isVertical: true
  }
])
</script>

<style scoped>
.play-list {
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: stretch;
  min-height: calc(100vh - 320px);
  z-index: 1;
}
.play-list .play-list-title {
  line-height: 0;
}
.play-list .video-items-wrapper {
  width: 100%;
  background-color: #fdd000;
  margin-top: -2px;
  padding: 1rem;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-gap: 0.5rem;
  grid-auto-rows: minmax(5rem, auto);
}
.video-list {
  display: flex;
  flex-wrap: wrap;
  min-height: calc(100vh - 320px);
  padding-top: 11.5%;
}
.video-item {
  overflow: hidden;
  display: inline-grid;
  grid-template-columns: 1fr;
  grid-template-rows: min-content max-content;
  grid-gap: 0.5rem 0;
}
.video-thumbnail,
.video-thumbnail-desc {
  object-fit: scale-down;
  -webkit-user-drag: none;
  -moz-user-select: none;
  user-select: none;
}
.video-item button {
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  line-height: 0;
}
.video-item:nth-child(4) {
  margin-top: -30%;
}
.video-item:nth-child(6) {
  margin-top: -25%;
}
.video-item:nth-child(7) {
  margin-top: -18%;
}
.video-item:nth-child(8) {
  margin-top: -12%;
}
.video-item:nth-child(10) {
  margin-top: -60%;
}
.video-item:nth-child(12) {
  margin-top: -25%;
}
.video-item:nth-child(13) {
  margin-top: -26%;
}
.video-item:nth-child(15) {
  margin-top: -30%;
}
.video-info {
  padding: 12px;
}
.video-title {
  margin: 0 0 8px 0;
  font-size: 1.1em;
}
.video-description {
  margin: 0;
  color: #666;
  font-size: 0.95em;
}

.video-popup .layer-popup .prev-btn {
  background-image: url('@/assets/2025/images/08_btn_left.png');
  position: absolute;
  top: 50%;
  left: 1%;
  transform: translate(0, -50%);
  background-repeat: no-repeat;
  background-position: center;
  background-size: auto 1.5rem;
  background-color: transparent;
  padding: 0.8rem 1rem;
}

.video-popup .layer-popup .next-btn {
  background-image: url('@/assets/2025/images/08_btn_right.png');
  position: absolute;
  top: 50%;
  right: 1%;
  transform: translate(0, -50%);
  background-repeat: no-repeat;
  background-position: center;
  background-size: auto 1.5rem;
  background-color: transparent;
  padding: 0.8rem 1rem;
}
.iframe-container {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 1;
  transition: opacity 0.5s ease-in-out; /* 트랜지션 시간 증가 */
  will-change: opacity; /* 트랜지션 성능 향상 */
  padding: 0; /* 패딩 제거하여 전체 공간 사용 */
}

/* .iframe-container의 기본 opacity가 이미 1이므로 fade-in 클래스는 불필요 */

.iframe-container.fade-out {
  opacity: 0;
}

.iframe-content {
  width: 100%;
  height: auto;
  margin: 0 auto;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  position: relative;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.3);
}

#video-list-iframe {
  width: 100%;
  height: 100%; /* 높이를 100%로 설정하여 부모 컨테이너에 맞춤 */
  display: block;
  position: relative;
  z-index: 2;
  /* 렌더링 품질 향상 */
  transform: translateZ(0);
  backface-visibility: hidden;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  will-change: transform;
}

/* Overlay styles */
.iframe-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 3;
  background-color: transparent;
  cursor: pointer;
}

@media (min-width: 640px) {
  .video-popup .layer-popup .prev-btn {
    left: 2.5rem;
    background-size: auto 2.5rem;
  }
  .video-popup .layer-popup .next-btn {
    right: 2.5rem;
    background-size: auto 2.5rem;
  }
}
</style>
